<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="extension-page">
    <!-- SEO-Optimized Hero Section -->
    <header class="hero">
      <div class="container">
        <h1 class="hero-title">{{ $t('extension.h1Title') }}</h1>
        <p class="hero-subtitle">{{ $t('seo.extension.description') }}</p>
        <div class="hero-actions">
          <a
            :href="heroCTAUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-primary btn-large chrome-store-btn"
          >
            {{ $t('extension.hero.addToChrome') }}
          </a>
          <router-link to="/pricing" class="btn btn-secondary">
            {{ $t('extension.hero.viewPricing') }}
          </router-link>
        </div>
        <div class="trust-indicators">
          <span class="trust-item">✓ {{ $t('extension.trust.free') }}</span>
          <span class="trust-item">✓ {{ $t('extension.trust.secure') }}</span>
          <span class="trust-item">✓ {{ $t('extension.trust.multilingual') }}</span>
        </div>
      </div>
    </header>

    <!-- Features Showcase Section -->
    <section class="features-showcase">
      <div class="container">
        <h2>{{ $t('extension.features.title') }}</h2>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">✍️</div>
            <h3>{{ $t('extension.features.grammar.title') }}</h3>
            <p>{{ $t('extension.features.grammar.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🔄</div>
            <h3>{{ $t('extension.features.rewrite.title') }}</h3>
            <p>{{ $t('extension.features.rewrite.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🌍</div>
            <h3>{{ $t('extension.features.translate.title') }}</h3>
            <p>{{ $t('extension.features.translate.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">📝</div>
            <h3>{{ $t('extension.features.summarize.title') }}</h3>
            <p>{{ $t('extension.features.summarize.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🎯</div>
            <h3>{{ $t('extension.features.contextMenu.title') }}</h3>
            <p>{{ $t('extension.features.contextMenu.description') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">⚡</div>
            <h3>{{ $t('extension.features.instant.title') }}</h3>
            <p>{{ $t('extension.features.instant.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works">
      <div class="container">
        <h2>{{ $t('extension.howItWorks.title') }}</h2>
        <div class="steps-grid">
          <div class="step">
            <div class="step-number">1</div>
            <h3>{{ $t('extension.howItWorks.step1.title') }}</h3>
            <p>{{ $t('extension.howItWorks.step1.description') }}</p>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <h3>{{ $t('extension.howItWorks.step2.title') }}</h3>
            <p>{{ $t('extension.howItWorks.step2.description') }}</p>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <h3>{{ $t('extension.howItWorks.step3.title') }}</h3>
            <p>{{ $t('extension.howItWorks.step3.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="use-cases">
      <div class="container">
        <h2>{{ $t('extension.useCases.title') }}</h2>
        <div class="use-cases-grid">
          <div class="use-case-card">
            <h3>{{ $t('extension.useCases.email.title') }}</h3>
            <p>{{ $t('extension.useCases.email.description') }}</p>
          </div>
          <div class="use-case-card">
            <h3>{{ $t('extension.useCases.social.title') }}</h3>
            <p>{{ $t('extension.useCases.social.description') }}</p>
          </div>
          <div class="use-case-card">
            <h3>{{ $t('extension.useCases.documents.title') }}</h3>
            <p>{{ $t('extension.useCases.documents.description') }}</p>
          </div>
          <div class="use-case-card">
            <h3>{{ $t('extension.useCases.forms.title') }}</h3>
            <p>{{ $t('extension.useCases.forms.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="faq">
      <div class="container">
        <h2>{{ $t('extension.faq.title') }}</h2>
        <div class="faq-list">
          <div class="faq-item">
            <h3>{{ $t('extension.faq.q1.question') }}</h3>
            <p>{{ $t('extension.faq.q1.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('extension.faq.q2.question') }}</h3>
            <p>{{ $t('extension.faq.q2.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('extension.faq.q3.question') }}</h3>
            <p>{{ $t('extension.faq.q3.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('extension.faq.q4.question') }}</h3>
            <p>{{ $t('extension.faq.q4.answer') }}</p>
          </div>
          <div class="faq-item">
            <h3>{{ $t('extension.faq.q5.question') }}</h3>
            <p>{{ $t('extension.faq.q5.answer') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA Section -->
    <section class="cta">
      <div class="container">
        <h2>{{ $t('extension.cta.title') }}</h2>
        <p>{{ $t('extension.cta.description') }}</p>
        <a
          :href="finalCTAUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-primary btn-large chrome-store-btn"
        >
          <svg class="chrome-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-.84 4.67h7.49l-3.87 6.7c-.51-.05-1.04-.08-1.58-.08-2.29 0-4.42.78-6.11 2.08l2.07-3.59c1.17-2.03 3.37-3.29 5.73-3.29.76 0 1.5.11 2.2.31l-5.94-.13zm-6.3 2.89l3.87 6.7c-.92 1.15-1.47 2.61-1.47 4.21 0 .58.07 1.14.21 1.68l-4.02-6.96c-.39-.68-.6-1.45-.6-2.26 0-2.43 1.97-4.4 4.4-4.4.23 0 .45.02.67.05l-3.06.98zm8.14 2.31c3.02 0 5.47 2.45 5.47 5.47s-2.45 5.47-5.47 5.47-5.47-2.45-5.47-5.47 2.45-5.47 5.47-5.47z"/>
          </svg>
          {{ $t('extension.cta.button') }}
        </a>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import { getChromeStoreUrl, CAMPAIGNS, CONTENT_IDS } from '@/utils/tracking'

const { t } = useI18n()

// Generate UTM-tracked Chrome Web Store URLs
const heroCTAUrl = computed(() =>
  getChromeStoreUrl({
    campaign: CAMPAIGNS.EXTENSION_LANDING,
    content: CONTENT_IDS.HERO_CTA,
  })
)

const finalCTAUrl = computed(() =>
  getChromeStoreUrl({
    campaign: CAMPAIGNS.EXTENSION_LANDING,
    content: CONTENT_IDS.FINAL_CTA,
  })
)

// SEO optimization for Extension page
useHead({
  title: () => t('seo.extension.title'),
  meta: [
    {
      name: 'description',
      content: () => t('seo.extension.description'),
    },
    {
      name: 'keywords',
      content: () => t('seo.extension.keywords'),
    },
    {
      name: 'robots',
      content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    },
    {
      name: 'author',
      content: 'n.writer',
    },
    {
      property: 'og:title',
      content: () => t('seo.extension.title'),
    },
    {
      property: 'og:description',
      content: () => t('seo.extension.description'),
    },
    {
      property: 'og:url',
      content: 'https://n-writer.app/extension',
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:site_name',
      content: 'n.writer',
    },
    {
      property: 'og:locale',
      content: 'en_US',
    },
    {
      property: 'og:locale:alternate',
      content: 'fr_FR',
    },
    {
      property: 'og:locale:alternate',
      content: 'es_ES',
    },
    {
      property: 'og:image',
      content: 'https://n-writer.app/images/og-extension.jpg',
    },
    {
      property: 'og:image:width',
      content: '1200',
    },
    {
      property: 'og:image:height',
      content: '630',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: () => t('seo.extension.title'),
    },
    {
      name: 'twitter:description',
      content: () => t('seo.extension.description'),
    },
    {
      name: 'twitter:image',
      content: 'https://n-writer.app/images/twitter-extension.jpg',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://n-writer.app/extension',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://n-writer.app',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Chrome Extension',
            item: 'https://n-writer.app/extension',
          },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: t('seo.extension.title'),
        description: t('seo.extension.description'),
        url: 'https://n-writer.app/extension',
        mainEntity: {
          '@type': 'SoftwareApplication',
          name: 'n.writer - AI Writing Assistant',
          applicationCategory: 'BrowserExtension',
          operatingSystem: 'Chrome',
          softwareVersion: '2.0',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            ratingCount: '850',
          },
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
          },
          featureList: [
            'AI Grammar Checker',
            'Text Rewriting',
            'Translation',
            'Summarization',
            'Spell Checking',
            'Context Menu Integration',
          ],
          screenshot: 'https://n-writer.app/images/extension-screenshot.jpg',
          downloadUrl: 'https://chromewebstore.google.com/detail/fjlfoaokggeemlcoemkhokccampibipm',
        },
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: t('extension.faq.q1.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('extension.faq.q1.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('extension.faq.q2.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('extension.faq.q2.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('extension.faq.q3.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('extension.faq.q3.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('extension.faq.q4.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('extension.faq.q4.answer'),
            },
          },
          {
            '@type': 'Question',
            name: t('extension.faq.q5.question'),
            acceptedAnswer: {
              '@type': 'Answer',
              text: t('extension.faq.q5.answer'),
            },
          },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Use n.writer Chrome Extension',
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: t('extension.howItWorks.step1.title'),
            text: t('extension.howItWorks.step1.description'),
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: t('extension.howItWorks.step2.title'),
            text: t('extension.howItWorks.step2.description'),
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: t('extension.howItWorks.step3.title'),
            text: t('extension.howItWorks.step3.description'),
          },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'n.writer',
        legalName: 'NoorWays',
        url: 'https://n-writer.app',
        logo: 'https://n-writer.app/images/noorwriter-logo.png',
        description:
          'n.writer is an AI-powered writing assistant that helps users improve their writing with grammar checking, text rewriting, translation, and more.',
        foundingDate: '2024',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Support',
          email: 'support@n-writer.app',
          url: 'https://n-writer.app/support',
        },
        sameAs: [
          'https://chromewebstore.google.com/detail/fjlfoaokggeemlcoemkhokccampibipm',
          'https://github.com/noorways',
        ],
        brand: {
          '@type': 'Brand',
          name: 'n.writer',
          logo: 'https://n-writer.app/images/noorwriter-logo.png',
        },
        offers: {
          '@type': 'Offer',
          name: 'n.writer Chrome Extension',
          price: '0',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url: 'https://chromewebstore.google.com/detail/fjlfoaokggeemlcoemkhokccampibipm',
        },
      }),
    },
  ],
})


</script>

<style scoped lang="scss">
.extension-page {
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

section {
  padding: 5rem 0;

  h2 {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 3rem;
    color: var(--nw-brand-slate);
    font-weight: 800;
  }
}

.hero {
  background: linear-gradient(135deg, var(--nw-brand-slate) 0%, #0f172a 100%);
  color: white;
  padding: 6rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  // Animated background orbs
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.3;
    animation: float 20s ease-in-out infinite;
  }

  &::before {
    width: 400px;
    height: 400px;
    background: var(--nw-primary);
    top: -100px;
    right: -100px;
  }

  &::after {
    width: 300px;
    height: 300px;
    background: var(--nw-primary-light);
    bottom: -50px;
    left: -50px;
    animation-delay: -10s;
  }

  &-title {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    line-height: 1.1;
    position: relative;
    z-index: 1;
  }

  &-subtitle {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 1;
  }

  &-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    position: relative;
    z-index: 1;
  }
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(30px, -30px) scale(1.05); }
  50% { transform: translate(-20px, 20px) scale(0.95); }
  75% { transform: translate(20px, 10px) scale(1.02); }
}

.chrome-store-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  .chrome-icon {
    width: 24px;
    height: 24px;
  }
}

.btn {
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &-primary {
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    color: white;
    box-shadow: 0 4px 15px var(--nw-primary-40);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px var(--nw-primary-40);
    }
  }

  &-secondary {
    background: rgba(255, 255, 255, 0.95);
    color: var(--nw-brand-slate);
    backdrop-filter: blur(10px);

    &:hover {
      background: white;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    }
  }

  &-large {
    padding: 1.25rem 2.5rem;
    font-size: 1.1rem;
  }
}

.trust-indicators {
  display: flex;
  gap: 2rem;
  justify-content: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.trust-item {
  font-size: 1rem;
  opacity: 0.95;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.feature-card {
  background: white;
  padding: 2rem;
  border-radius: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  border: 1px solid var(--nw-border-light);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px var(--nw-primary-20);
    border-color: var(--nw-primary-20);
  }

  .feature-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    display: inline-block;
    padding: 1rem;
    background: linear-gradient(135deg, var(--nw-primary-10) 0%, rgba(30, 64, 175, 0.05) 100%);
    border-radius: 1rem;
  }

  h3 {
    margin-bottom: 1rem;
    color: var(--nw-brand-slate);
    font-size: 1.25rem;
    font-weight: 700;
  }

  p {
    color: var(--nw-text-secondary);
    line-height: 1.6;
  }
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.step {
  text-align: center;
  padding: 2.5rem 2rem;
  background: white;
  border-radius: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--nw-border-light);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  }

  &-number {
    width: 70px;
    height: 70px;
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    font-weight: 800;
    margin: 0 auto 1.5rem;
    box-shadow: 0 8px 20px var(--nw-primary-40);
  }

  h3 {
    margin-bottom: 1rem;
    color: var(--nw-brand-slate);
    font-weight: 700;
  }

  p {
    color: var(--nw-text-secondary);
    line-height: 1.6;
  }
}

.demo-section {
  background: #f8f9fa;

  .demo-subtitle {
    text-align: center;
    color: #666;
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }

  .demo-placeholder {
    max-width: 800px;
    margin: 0 auto;
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  .demo-image-placeholder {
    svg {
      width: 100%;
      height: auto;
    }
  }
}

.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.use-case-card {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }

  h3 {
    margin-bottom: 1rem;
    color: #1e40af;
  }

  p {
    color: #666;
    line-height: 1.6;
  }
}

.testimonial-card {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  .stars {
    color: #fbbf24;
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }

  .testimonial-text {
    font-style: italic;
    color: #666;
    line-height: 1.6;
    margin-bottom: 1rem;
  }

  .testimonial-author {
    font-weight: 600;
    color: #1e40af;
  }
}

.faq-list {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  margin-bottom: 2rem;
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  h3 {
    margin-bottom: 1rem;
    color: #1e40af;
    font-size: 1.1rem;
  }

  p {
    color: #666;
    line-height: 1.6;
  }
}

.cta {
  background: linear-gradient(135deg, var(--nw-brand-slate) 0%, #0f172a 100%);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    background: var(--nw-primary);
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.2;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
  }

  h2 {
    color: white;
    position: relative;
    z-index: 1;
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    position: relative;
    z-index: 1;
  }

  .btn {
    position: relative;
    z-index: 1;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-actions {
    flex-direction: column;
    align-items: center;
  }

  .trust-indicators {
    flex-direction: column;
    gap: 1rem;
  }

  section h2 {
    font-size: 2rem;
  }
}
</style>

<!-- Dark theme overrides - unscoped to properly target html.dark -->
<style lang="scss">
html.dark {
  .extension-page {
    // Section headings
    section h2 {
      color: #F1F5F9;
    }

    // Features showcase section
    .features-showcase {
      background: #0F172A;
    }

    .feature-card {
      background: #1E293B;
      border-color: #334155;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

      &:hover {
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(30, 64, 175, 0.3);
        border-color: rgba(30, 64, 175, 0.3);
      }

      .feature-icon {
        background: linear-gradient(135deg, rgba(30, 64, 175, 0.2) 0%, rgba(30, 64, 175, 0.1) 100%);
      }

      h3 {
        color: #F1F5F9;
      }

      p {
        color: #CBD5E1;
      }
    }

    // How it works section
    .how-it-works {
      background: #1E293B;
    }

    .step {
      background: #0F172A;
      border-color: #334155;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

      &:hover {
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.4);
      }

      h3 {
        color: #F1F5F9;
      }

      p {
        color: #CBD5E1;
      }
    }

    // Use cases section
    .use-cases {
      background: #0F172A;
    }

    .use-case-card {
      background: #1E293B;
      border: 1px solid #334155;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

      &:hover {
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
      }

      h3 {
        color: #F1F5F9;
      }

      p {
        color: #CBD5E1;
      }
    }

    // FAQ section
    .faq {
      background: #1E293B;
    }

    .faq-item {
      background: #0F172A;
      border: 1px solid #334155;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

      h3 {
        color: #F1F5F9;
      }

      p {
        color: #CBD5E1;
      }
    }

    // Demo section
    .demo-section {
      background: #1E293B;

      .demo-subtitle {
        color: #CBD5E1;
      }

      .demo-placeholder {
        background: #0F172A;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      }
    }

    // Testimonial card
    .testimonial-card {
      background: #1E293B;
      border: 1px solid #334155;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

      .testimonial-text {
        color: #CBD5E1;
      }

      .testimonial-author {
        color: #F1F5F9;
      }
    }
  }
}
</style>

